import { CHARACTER_SLOTS, type CharacterShape, type CharacterSlotId } from '@momentum/shared';

/**
 * Small shared shape+colour glyph for a character slot. Used inline by `Board2D.tsx` (nested inside its own
 * `<svg>`) and standalone by the `poc-choice` and `fixture-team-2v2` renderers (participant badges). Colour
 * comes from the CSS custom property named by `CHARACTER_SLOTS[slot].colorToken` with a hard-coded fallback
 * hex so the badge still reads correctly before `theme.css` defines the token.
 */
const FALLBACK_HEX: Record<CharacterShape, string> = {
  square: '#c9a6f5',
  oval: '#1e7cf0',
  triangle: '#ff7a6b',
  hexagon: '#ffcc33',
  diamond: '#4cc38a',
  circle: '#2b2d33',
};

function hexagonPoints(cx: number, cy: number, r: number): string {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 90);
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  }).join(' ');
}

function ShapeGlyph({ shape, fill }: { shape: CharacterShape; fill: string }) {
  switch (shape) {
    case 'circle':
      return <circle cx={20} cy={20} r={16} fill={fill} />;
    case 'oval':
      return <ellipse cx={20} cy={20} rx={18} ry={13} fill={fill} />;
    case 'square':
      return <rect x={5} y={5} width={30} height={30} rx={6} fill={fill} />;
    case 'diamond':
      return <rect x={9} y={9} width={22} height={22} rx={3} fill={fill} transform="rotate(45 20 20)" />;
    case 'triangle':
      return <polygon points="20,4 36,34 4,34" fill={fill} />;
    case 'hexagon':
      return <polygon points={hexagonPoints(20, 20, 17)} fill={fill} />;
  }
}

export type BadgeEmphasis = 'none' | 'you' | 'turn';

export interface CharacterBadgeProps {
  slot: CharacterSlotId | null;
  size?: number;
  /** Accessible name for the badge (usually the participant's display name). */
  title: string;
  /** `'you'` draws an accent ring (this is your token/participant); `'turn'` draws a dashed highlight ring. */
  emphasis?: BadgeEmphasis;
}

/** Shape + colour glyph for a character slot. `null` (no slot chosen yet) renders a neutral charcoal circle. */
export function CharacterBadge({ slot, size = 32, title, emphasis = 'none' }: CharacterBadgeProps) {
  const shape: CharacterShape = slot ? CHARACTER_SLOTS[slot].shape : 'circle';
  const colorToken = slot ? CHARACTER_SLOTS[slot].colorToken : 'char-charcoal';
  const fill = `var(--${colorToken}, ${FALLBACK_HEX[shape]})`;
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" role="img" aria-label={title}>
      {emphasis === 'you' && (
        <circle cx={20} cy={20} r={18.5} fill="none" stroke="var(--accent, #1a73ff)" strokeWidth={2.5} />
      )}
      {emphasis === 'turn' && (
        <circle cx={20} cy={20} r={18.5} fill="none" stroke="var(--highlight, #ffcc00)" strokeWidth={2.5} strokeDasharray="4 3" />
      )}
      <ShapeGlyph shape={shape} fill={fill} />
    </svg>
  );
}
